import type { DeletionCandidate } from "./types";
import { deletionCandidatesToTargets } from "./deletionCandidatesToTargets";
import type { TestTarget as OperationLogTestTarget } from "../../services/operationLogService";

/**
 * 削除候補から「削除対象ページ」を構築する。
 *
 * ルート Service（type=service, parentId なし）単位でグループ化し、
 * 各 Service について対応する削除候補ツリー全体を 1 ページとして生成する。
 * ルート Service に属さない孤児削除候補（どのルート Service の配下にもならないもの）は
 * 「その他の削除対象」ページにまとめる。
 *
 * 生成された PageResult の test_targets は operation="DELETE" のターゲット群。
 * group_index は groupIndexOffset から始まる連番（既存 pageResults との衝突を避ける）。
 */
export interface DeletionPageResult {
  group_index: number;
  path: string;
  test_targets: OperationLogTestTarget[];
  usecases: [];
  is_deletion_page: true;
}

export function deletionCandidatesToPageResults(
  candidates: DeletionCandidate[],
  groupIndexOffset: number,
): DeletionPageResult[] {
  if (candidates.length === 0) return [];

  // ルート Service（type=service かつ parentId なし）を抽出
  const rootServices = candidates.filter(
    (c) => c.type === "service" && !c.parentId,
  );

  // 全候補を id / 親 id でマップ
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const childrenOf = new Map<string, DeletionCandidate[]>();
  for (const c of candidates) {
    if (!c.parentId) continue;
    const siblings = childrenOf.get(c.parentId) ?? [];
    siblings.push(c);
    childrenOf.set(c.parentId, siblings);
  }

  // ある id を祖先にもつ候補をすべて収集（BFS）
  const collectSubtree = (rootId: string): DeletionCandidate[] => {
    const result: DeletionCandidate[] = [];
    const visited = new Set<string>();
    const queue = [rootId];
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      const node = byId.get(id);
      if (node) result.push(node);
      for (const child of childrenOf.get(id) ?? []) {
        if (!visited.has(child.id)) queue.push(child.id);
      }
    }
    return result;
  };

  const pages: DeletionPageResult[] = [];
  const assignedIds = new Set<string>();

  for (const svc of rootServices) {
    const subtree = collectSubtree(svc.id);
    subtree.forEach((c) => assignedIds.add(c.id));
    pages.push({
      group_index: groupIndexOffset + pages.length,
      path: svc.name,
      test_targets: deletionCandidatesToTargets(subtree),
      usecases: [],
      is_deletion_page: true,
    });
  }

  // 孤児（どのルート Service のサブツリーにも属さない）候補
  const orphans = candidates.filter((c) => !assignedIds.has(c.id));
  if (orphans.length > 0) {
    pages.push({
      group_index: groupIndexOffset + pages.length,
      path: "その他の削除対象",
      test_targets: deletionCandidatesToTargets(orphans),
      usecases: [],
      is_deletion_page: true,
    });
  }

  return pages;
}
